// FIXME: entities에서 features 참고하면 안됨.
import type { TFunction } from 'i18next';

import {
  CHART_CALCULATION,
  CHART_COLOR_SCALE,
  CHART_FIELD,
  CHART_FIELD_ITEM_SELECTION_METHOD,
  CHART_GRAPH,
  CHART_GRAPH_DIRECTION,
  CHART_METRIC,
  CHART_METRIC_VIEW_TYPE,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import { FeedbackSentiment } from '@/types/feedbackConst';
import { PRODUCT_COLUMNS } from '@/types/filterKeyConst';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';

import type { RowBlueprint, TemplateChartDef } from '../../model/types';

// --- 헬퍼 상수 ---

/** sentiment_detail 필터 키 (API response 기준) */
const SENTIMENT_DETAIL_KEY = 'sentiment_detail';

/** useTranslation('dashboard-template')에서 받은 t 함수 타입 */
export type DashboardTemplateTFn = TFunction<'dashboard-template'>;

// --- 메타데이터 ---

export const TEMPLATE_ID = 'product-sentiment-topic';

export const getTemplateName = (t: DashboardTemplateTFn) =>
  t('Product_Sentiment_Topic_Analysis_Dashboard');

export const getTemplateDescription = (t: DashboardTemplateTFn) =>
  t('Analyze_review_counts_sentiment_ratios_and_AI_topics');

// --- Chart Definitions ---

/**
 * 템플릿 차트 정의 (10개)
 *
 * Section 1 (Data Overview): chart 0~3
 * - 전체 리뷰 개수, 긍부정 비율, 제품별 리뷰 개수, Wordcloud
 *
 * Section 2 (AI Topic Analysis): chart 4~9
 * - 제품별 리뷰 개수, 제품별 긍부정 비율, AI Topic Positive/Negative, Wordcloud Positive/Negative
 */
export const getTemplateCharts = (
  t: DashboardTemplateTFn,
): TemplateChartDef[] => [
  // --- Section 1: Data Overview ---

  // chart 0: 전체 리뷰 개수 (TABLE, horizontal)
  {
    nameTemplate: t('Total_Review_Count'),
    chart: {
      type: CHART_TYPE.SINGLE_AXIS,
      graph: CHART_GRAPH.TABLE,
      graph_direction: CHART_GRAPH_DIRECTION.HORIZONTAL,
      field: { type: CHART_FIELD.FEEDBACK_ALL, entire: true },
      selected_field: { method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5 },
      historical: { frequency: null, value: false },
      series: [
        {
          measure: {
            metric: CHART_METRIC.FEEDBACK,
            calculation: CHART_CALCULATION.COUNT,
          },
          options: { view_type: CHART_METRIC_VIEW_TYPE.COUNT },
        },
      ],
    },
    mapping: {
      filterSourceFields: [PRODUCT_COLUMNS.PRODUCT],
      termSourceField: 'dateTerm',
    },
  },

  // chart 1: 전체 리뷰 긍부정 비율 (PIE)
  {
    nameTemplate: t('Overall_Sentiment_Ratio'),
    chart: {
      type: CHART_TYPE.SINGLE_AXIS,
      graph: CHART_GRAPH.PIE,
      graph_direction: CHART_GRAPH_DIRECTION.HORIZONTAL,
      field: {
        type: CHART_FIELD.FEEDBACK_SENTIMENT_DETAIL,
        entire: false,
        candidates: [
          FeedbackSentiment.POSITIVE,
          FeedbackSentiment.NEUTRAL,
          FeedbackSentiment.NEGATIVE,
        ],
      },
      selected_field: {
        method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5,
        items: [
          FeedbackSentiment.POSITIVE,
          FeedbackSentiment.NEUTRAL,
          FeedbackSentiment.NEGATIVE,
        ],
      },
      historical: { frequency: null, value: false },
      series: [
        {
          measure: {
            metric: CHART_METRIC.FEEDBACK,
            calculation: CHART_CALCULATION.COUNT,
          },
          options: { view_type: CHART_METRIC_VIEW_TYPE.PERCENTAGE },
        },
      ],
    },
    mapping: {
      filterSourceFields: [PRODUCT_COLUMNS.PRODUCT],
      termSourceField: 'dateTerm',
    },
  },

  // chart 2: 제품별 리뷰 개수 (BAR, horizontal)
  {
    nameTemplate: t('Reviews_by_Product'),
    chart: {
      type: CHART_TYPE.SINGLE_AXIS,
      graph: CHART_GRAPH.BAR,
      graph_direction: CHART_GRAPH_DIRECTION.HORIZONTAL,
      field: { type: CHART_FIELD.PRODUCT_PRODUCT, entire: false },
      selected_field: {
        method: CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED,
      },
      historical: { frequency: null, value: false },
      series: [
        {
          measure: {
            metric: CHART_METRIC.FEEDBACK,
            calculation: CHART_CALCULATION.COUNT,
          },
        },
      ],
    },
    mapping: {
      termSourceField: 'dateTerm',
      candidatesSourceField: PRODUCT_COLUMNS.PRODUCT,
      itemsSourceField: PRODUCT_COLUMNS.PRODUCT,
    },
  },

  // chart 3: Wordcloud
  {
    nameTemplate: t('Wordcloud'),
    chart: {
      type: CHART_TYPE.SINGLE_AXIS,
      historical: { frequency: null, value: false },
      graph: CHART_GRAPH.WORD_CLOUD,
      field: { type: CHART_FIELD.FEEDBACK_KEYWORD, entire: true, candidates: [] },
    },
    mapping: {
      filterSourceFields: [PRODUCT_COLUMNS.PRODUCT],
      termSourceField: 'dateTerm',
    },
  },

  // --- Section 2: AI Topic Analysis ---

  // chart 4: 제품별 리뷰 개수 (BAR, vertical, color_scale)
  {
    nameTemplate: t('Reviews_by_Product'),
    chart: {
      type: CHART_TYPE.SINGLE_AXIS,
      graph: CHART_GRAPH.BAR,
      graph_direction: CHART_GRAPH_DIRECTION.VERTICAL,
      field: { type: CHART_FIELD.PRODUCT_PRODUCT, entire: false },
      selected_field: { method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5 },
      historical: { frequency: null, value: false },
      series: [
        {
          measure: {
            metric: CHART_METRIC.FEEDBACK,
            calculation: CHART_CALCULATION.COUNT,
          },
          options: {
            color_scale: CHART_COLOR_SCALE.WHITE_TO_GREEN,
          },
        },
      ],
    },
    mapping: {
      termSourceField: 'dateTerm',
      candidatesSourceField: PRODUCT_COLUMNS.PRODUCT,
      itemsSourceField: PRODUCT_COLUMNS.PRODUCT,
    },
  },

  // chart 5: 제품별 긍부정 비율 (CROSS_AXIS, BAR_STACKED, horizontal, PERCENTAGE)
  {
    nameTemplate: t('Sentiment_Ratio_by_Product'),
    chart: {
      type: CHART_TYPE.CROSS_AXIS,
      graph: CHART_GRAPH.BAR_STACKED,
      graph_direction: CHART_GRAPH_DIRECTION.HORIZONTAL,
      field: { type: CHART_FIELD.PRODUCT_PRODUCT, entire: false },
      selected_field: { method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5 },
      historical: { frequency: null, value: false },
      series: [
        {
          cross_field: {
            type: CHART_FIELD.FEEDBACK_SENTIMENT_DETAIL,
            entire: false,
            candidates: [
              FeedbackSentiment.NEGATIVE,
              FeedbackSentiment.NEUTRAL,
              FeedbackSentiment.POSITIVE,
            ],
          },
          measure: {
            metric: CHART_METRIC.FEEDBACK,
            calculation: CHART_CALCULATION.COUNT,
          },
          options: { view_type: CHART_METRIC_VIEW_TYPE.PERCENTAGE },
        },
      ],
    },
    mapping: {
      termSourceField: 'dateTerm',
      candidatesSourceField: PRODUCT_COLUMNS.PRODUCT,
      itemsSourceField: PRODUCT_COLUMNS.PRODUCT,
    },
  },

  // chart 6: AI Topic 분석 - Positive (CROSS_AXIS, TABLE, color_scale)
  {
    nameTemplate: t('AI_Topic_Analysis_Positive'),
    chart: {
      type: CHART_TYPE.CROSS_AXIS,
      graph: CHART_GRAPH.TABLE,
      graph_direction: CHART_GRAPH_DIRECTION.VERTICAL,
      field: { type: CHART_FIELD.TAXONOMY_TOPIC, entire: false },
      selected_field: { method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5 },
      historical: { frequency: null, value: false },
      series: [
        {
          cross_field: {
            type: CHART_FIELD.PRODUCT_PRODUCT,
            entire: false,
          },
          measure: {
            metric: CHART_METRIC.FEEDBACK,
            calculation: CHART_CALCULATION.COUNT,
          },
          options: {
            view_type: CHART_METRIC_VIEW_TYPE.COUNT,
            color_scale: CHART_COLOR_SCALE.WHITE_TO_GREEN,
          },
        },
      ],
    },
    mapping: {
      termSourceField: 'dateTerm',
      candidatesSourceField: 'taxonomy_topic_id',
      crossFieldCandidatesSourceField: PRODUCT_COLUMNS.PRODUCT,
      extraFilter: {
        [SENTIMENT_DETAIL_KEY]: [
          {
            op: STRUCTURED_FILTER_OPERATOR.OR,
            values: [FeedbackSentiment.POSITIVE],
          },
        ],
      },
    },
  },

  // chart 7: AI Topic 비교 분석 - Negative (CROSS_AXIS, TABLE, color_scale)
  {
    nameTemplate: t('AI_Topic_Comparison_Negative'),
    chart: {
      type: CHART_TYPE.CROSS_AXIS,
      graph: CHART_GRAPH.TABLE,
      graph_direction: CHART_GRAPH_DIRECTION.VERTICAL,
      field: { type: CHART_FIELD.TAXONOMY_TOPIC, entire: false },
      selected_field: { method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5 },
      historical: { frequency: null, value: false },
      series: [
        {
          cross_field: {
            type: CHART_FIELD.PRODUCT_PRODUCT,
            entire: false,
          },
          measure: {
            metric: CHART_METRIC.FEEDBACK,
            calculation: CHART_CALCULATION.COUNT,
          },
          options: {
            view_type: CHART_METRIC_VIEW_TYPE.COUNT,
            color_scale: CHART_COLOR_SCALE.WHITE_TO_RED,
          },
        },
      ],
    },
    mapping: {
      termSourceField: 'dateTerm',
      candidatesSourceField: 'taxonomy_topic_id',
      crossFieldCandidatesSourceField: PRODUCT_COLUMNS.PRODUCT,
      extraFilter: {
        [SENTIMENT_DETAIL_KEY]: [
          {
            op: STRUCTURED_FILTER_OPERATOR.OR,
            values: [FeedbackSentiment.NEGATIVE],
          },
        ],
      },
    },
  },

  // chart 8: Wordcloud - Positive
  {
    nameTemplate: t('Wordcloud_Positive'),
    chart: {
      type: CHART_TYPE.SINGLE_AXIS,
      historical: { frequency: null, value: false },
      graph: CHART_GRAPH.WORD_CLOUD,
      field: { type: CHART_FIELD.FEEDBACK_KEYWORD, entire: true, candidates: [] },
    },
    mapping: {
      filterSourceFields: [PRODUCT_COLUMNS.PRODUCT],
      termSourceField: 'dateTerm',
      extraFilter: {
        [SENTIMENT_DETAIL_KEY]: [
          {
            op: STRUCTURED_FILTER_OPERATOR.OR,
            values: [FeedbackSentiment.POSITIVE],
          },
        ],
      },
    },
  },

  // chart 9: Wordcloud - Negative
  {
    nameTemplate: t('Wordcloud_Negative'),
    chart: {
      type: CHART_TYPE.SINGLE_AXIS,
      historical: { frequency: null, value: false },
      graph: CHART_GRAPH.WORD_CLOUD,
      field: { type: CHART_FIELD.FEEDBACK_KEYWORD, entire: true, candidates: [] },
    },
    mapping: {
      filterSourceFields: [PRODUCT_COLUMNS.PRODUCT],
      termSourceField: 'dateTerm',
      extraFilter: {
        [SENTIMENT_DETAIL_KEY]: [
          {
            op: STRUCTURED_FILTER_OPERATOR.OR,
            values: [FeedbackSentiment.NEGATIVE],
          },
        ],
      },
    },
  },
];

// --- Layout Blueprint ---

export const getTemplateLayout = (t: DashboardTemplateTFn): RowBlueprint[] => [
  // Row 5: "1. Data Overview" 설명
  {
    type: 'text',
    text: `<h3><strong>${t('Data_Overview')}</strong></h3><ul><li>${t('Data_Overview_description')}</li></ul>`,
  },

  // Row 1: chart 0~3 (4개 차트)
  {
    type: 'chart',
    chartIndices: [0, 1, 2, 3],
    height: '544px',
  },

  // Row 0: "2. AI Topic Analysis" 설명
  {
    type: 'text',
    text: `<h3>${t('AI_Topic_Analysis')}</h3><p><br></p><ul><li>${t('AI_Topic_Analysis_bullet_1')}</li><li>${t('AI_Topic_Analysis_bullet_2')}</li><li>${t('AI_Topic_Analysis_bullet_3')}</li></ul>`,
  },

  // Row 2: chart 4, 5 (2개 차트)
  {
    type: 'chart',
    chartIndices: [4, 5],
    height: '465px',
  },

  // Row 6: chart 6, 7 (2개 차트)
  {
    type: 'chart',
    chartIndices: [6, 7],
  },

  // Row 4: chart 8, 9 (Wordcloud Positive/Negative)
  {
    type: 'chart',
    chartIndices: [8, 9],
    height: '515px',
  },
];
